import {
  Disclaimer,
  Div,
  H5,
  lightTheme,
  LocationServiceValue,
  Modal,
  Separator,
} from '@vgrubs/components';
import {
  EXPENSE_LINES,
  INCOME_LINES,
  MoneyLineSpec,
  periodLabel,
} from '../helpers/serviceValueLines';
import { MoneyLine } from './MoneyLine';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  value: LocationServiceValue;
};

/**
 * Every line behind "vGrubs Profit": what we made from the location per
 * service, what it cost us, and the difference. Lines at $0 are left out so
 * the few that matter are readable; the totals always show.
 */
export const CompanyValueModal = ({ isOpen, onClose, value }: Props) => {
  const { income, expenses, profit } = value.company;

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      logo={false}
      header="vGrubs Profit"
      subtitle={periodLabel(value.from, value.to)}
      textAlign="left"
      width="520px"
    >
      <Section
        title="Income"
        lines={INCOME_LINES}
        values={income}
        total={income.total}
        totalLabel="Total income"
      />

      <Separator $m="16px 0" />

      <Section
        title="Expenses"
        lines={EXPENSE_LINES}
        values={expenses}
        total={expenses.total}
        totalLabel="Total expenses"
      />

      <Separator $m="16px 0" />

      <MoneyLine
        label="Profit"
        amount={profit}
        strong
        color={profit < 0 ? lightTheme.primary : lightTheme.green}
      />
    </Modal>
  );
};

type SectionProps<K extends string> = {
  title: string;
  lines: MoneyLineSpec<K>[];
  values: Record<K, number>;
  total: number;
  totalLabel: string;
};

const Section = <K extends string>({
  title,
  lines,
  values,
  total,
  totalLabel,
}: SectionProps<K>) => {
  const shown = lines.filter((line) => values[line.key] !== 0);

  return (
    <Div $flex $column $gap="8px">
      <H5 $m="0">{title}</H5>
      {shown.length === 0 && (
        <Disclaimer $m="0" $color={lightTheme.lightGray}>
          Nothing in this period.
        </Disclaimer>
      )}
      {shown.map((line) => (
        <MoneyLine
          key={line.key}
          label={line.label}
          info={line.info}
          amount={values[line.key]}
        />
      ))}
      <MoneyLine label={totalLabel} amount={total} strong />
    </Div>
  );
};
